import { isCancelled, LANGS, type Lang, type MailTemplateBody } from '@reditelji/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { VolunteerRow } from '../api/volunteers';
import { EMPTY_FILTERS, NOTICE_STATES, type NoticeFilters } from './noticeFilters';

/**
 * The obvestilo being written, kept in this browser until it is sent.
 *
 * Nothing is stored on the server before the send — an obvestilo is written
 * once, for one send — but losing half an hour of wording to a reload would be
 * worse than that promise is worth, so the draft lives in localStorage, per
 * coordinator, and goes the moment the send is accepted.
 */
export interface NoticeDraft {
  v: 1;
  sl: MailTemplateBody;
  en: MailTemplateBody;
  filters: NoticeFilters;
  picked: string[];
  includeCancelled: boolean;
  /** People handed over from Prejemniki who are not coming — ticked back in with the opt-in. */
  handoffCancelled: string[];
  /** Housemates added from the confirmation, kept while their housemate is ticked. */
  addedHousemates: string[];
}

export const emptyDraft = (): NoticeDraft => ({
  v: 1,
  sl: { subject: '', body: '' },
  en: { subject: '', body: '' },
  filters: EMPTY_FILTERS,
  picked: [],
  includeCancelled: false,
  handoffCancelled: [],
  addedHousemates: [],
});

const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null;
const strings = (x: unknown): string[] =>
  Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string') : [];
const body = (x: unknown): MailTemplateBody =>
  isRecord(x)
    ? {
        subject: typeof x.subject === 'string' ? x.subject : '',
        body: typeof x.body === 'string' ? x.body : '',
      }
    : { subject: '', body: '' };

/**
 * A stored draft, or an empty one for anything that is not recognisably ours:
 * nothing, broken JSON, another version of the shape. Every field is read on
 * its own, so one odd value costs that field and not the whole wording.
 */
export function parseDraft(raw: string | null): NoticeDraft {
  if (raw === null) return emptyDraft();
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return emptyDraft();
  }
  if (!isRecord(data) || data.v !== 1) return emptyDraft();
  const f = isRecord(data.filters) ? data.filters : {};
  return {
    v: 1,
    sl: body(data.sl),
    en: body(data.en),
    filters: {
      query: typeof f.query === 'string' ? f.query : '',
      states: strings(f.states).filter((s): s is NoticeFilters['states'][number] =>
        (NOTICE_STATES as readonly string[]).includes(s),
      ),
      dayIds: strings(f.dayIds),
      sectorIds: strings(f.sectorIds),
      postIds: strings(f.postIds),
      shifts: f.shifts === 'z' || f.shifts === 'brez' ? f.shifts : 'vsi',
      lang: f.lang === 'sl' || f.lang === 'en' ? f.lang : 'vsi',
      tagIds: strings(f.tagIds),
    },
    picked: strings(data.picked),
    includeCancelled: data.includeCancelled === true,
    handoffCancelled: strings(data.handoffCancelled),
    addedHousemates: strings(data.addedHousemates),
  };
}

/** Whether a coordinator has written anything worth keeping. */
export function draftHasText(d: NoticeDraft): boolean {
  return LANGS.some((l) => d[l].subject.trim() !== '' || d[l].body.replace(/<[^>]*>/g, '').trim() !== '');
}

/** What the Prejemniki handoff leaves out of the selection, for the hint that says so. */
export interface HandoffNote {
  cancelled: number;
  noEmail: number;
}

/**
 * The Prejemniki selection as the draft's recipients: the text stays, the
 * filters start over, and only people an obvestilo can reach are ticked.
 * Somebody not coming is kept aside, so turning the opt-in on brings exactly
 * them back; somebody without an address cannot be ticked at all.
 */
export function handoff(
  draft: NoticeDraft,
  ids: readonly string[],
  roster: readonly VolunteerRow[],
): { draft: NoticeDraft; note: HandoffNote } {
  const byId = new Map(roster.map((v) => [v.id, v]));
  const people = [...new Set(ids)].map((id) => byId.get(id)).filter((v) => v !== undefined);
  const noEmail = people.filter((v) => !v.email);
  const cancelled = people.filter((v) => v.email && isCancelled(v));
  const picked = people.filter((v) => v.email && !isCancelled(v));
  return {
    draft: {
      ...draft,
      filters: EMPTY_FILTERS,
      picked: picked.map((v) => v.id),
      includeCancelled: false,
      handoffCancelled: cancelled.map((v) => v.id),
      addedHousemates: [],
    },
    note: { cancelled: cancelled.length, noEmail: noEmail.length },
  };
}

/** The opt-in for people not coming — on, it ticks back whoever the handoff kept aside. */
export function withIncludeCancelled(draft: NoticeDraft, on: boolean): NoticeDraft {
  if (!on) return { ...draft, includeCancelled: false };
  return {
    ...draft,
    includeCancelled: true,
    picked: [...new Set([...draft.picked, ...draft.handoffCancelled])],
    handoffCancelled: [],
  };
}

/**
 * What another tab wrote, when it differs from what this tab holds — null when
 * the two already agree.
 *
 * Compared as parsed drafts, not as stored strings: every tab re-writes what it
 * adopted in its own serialisation, and reacting to a difference in spelling
 * alone would bounce one draft between two tabs and re-seed both editors.
 */
export function adoptFromStorage(current: NoticeDraft, stored: string | null): NoticeDraft | null {
  const incoming = parseDraft(stored);
  const same = JSON.stringify(incoming) === JSON.stringify(parseDraft(JSON.stringify(current)));
  return same ? null : incoming;
}

const storageKey = (coordinatorId: string) => `rr_obvestilo_draft:${coordinatorId}`;

function readStored(coordinatorId: string | null): NoticeDraft {
  if (coordinatorId === null) return emptyDraft();
  try {
    return parseDraft(localStorage.getItem(storageKey(coordinatorId)));
  } catch {
    return emptyDraft();
  }
}

function writeStored(coordinatorId: string, draft: NoticeDraft): void {
  try {
    const empty = !draftHasText(draft) && draft.picked.length === 0;
    if (empty) localStorage.removeItem(storageKey(coordinatorId));
    else localStorage.setItem(storageKey(coordinatorId), JSON.stringify(draft));
  } catch {
    // A full or blocked storage costs the draft's survival across a reload, nothing more.
  }
}

interface Held {
  coordinatorId: string | null;
  draft: NoticeDraft;
  /** Bumped whenever the text is replaced rather than typed, so the editor re-seeds. */
  seed: number;
}

export function useNoticeDraft(coordinatorId: string | null) {
  // Read before the first render: the editor takes its content once, when it mounts.
  const [held, setHeld] = useState<Held>(() => ({
    coordinatorId,
    draft: readStored(coordinatorId),
    seed: 0,
  }));
  // Signed in after the first render — the session was still loading.
  if (held.coordinatorId !== coordinatorId) {
    setHeld({ coordinatorId, draft: readStored(coordinatorId), seed: held.seed + 1 });
  }

  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(held);
  useEffect(() => {
    latest.current = held;
  }, [held]);

  const flush = useCallback(() => {
    if (pending.current === null) return;
    clearTimeout(pending.current);
    pending.current = null;
    const { coordinatorId: id, draft } = latest.current;
    if (id !== null) writeStored(id, draft);
  }, []);

  useEffect(() => {
    if (held.coordinatorId === null) return;
    if (pending.current !== null) clearTimeout(pending.current);
    const { coordinatorId: id, draft } = held;
    pending.current = setTimeout(() => {
      pending.current = null;
      writeStored(id, draft);
    }, 500);
  }, [held]);

  /*
   * Another tab of the same coordinator changed the draft — most importantly,
   * sent it and removed it. This tab takes that over rather than holding on to
   * its own copy: otherwise its next write would put a sent obvestilo back,
   * with its recipients, one click away from going out a second time. The
   * browser only tells other tabs, so a tab never hears its own writes.
   */
  useEffect(() => {
    const id = held.coordinatorId;
    if (id === null) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey(id) && e.key !== null) return;
      // `key` is null when the whole storage was cleared.
      const next = adoptFromStorage(latest.current.draft, e.key === null ? null : e.newValue);
      if (next === null) return;
      if (pending.current !== null) {
        clearTimeout(pending.current);
        pending.current = null;
      }
      setHeld((h) => ({ ...h, draft: next, seed: h.seed + 1 }));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [held.coordinatorId]);

  useEffect(() => {
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [flush]);

  const update = useCallback(
    (fn: (d: NoticeDraft) => NoticeDraft, reseed = false) =>
      setHeld((h) => ({ ...h, draft: fn(h.draft), seed: reseed ? h.seed + 1 : h.seed })),
    [],
  );

  return {
    draft: held.draft,
    seed: held.seed,
    /** Typing: the editor already shows it. */
    setBody: useCallback(
      (lang: Lang, patch: Partial<MailTemplateBody>) =>
        update((d) => ({ ...d, [lang]: { ...d[lang], ...patch } })),
      [update],
    ),
    /** A wholesale replacement — an AI translation — which the open editor has to be told about. */
    replaceBody: useCallback(
      (lang: Lang, next: MailTemplateBody) => update((d) => ({ ...d, [lang]: next }), true),
      [update],
    ),
    setFilters: useCallback(
      (filters: NoticeFilters) => update((d) => ({ ...d, filters })),
      [update],
    ),
    setPicked: useCallback(
      (fn: (picked: string[]) => string[]) => update((d) => ({ ...d, picked: fn(d.picked) })),
      [update],
    ),
    setIncludeCancelled: useCallback(
      (on: boolean) => update((d) => withIncludeCancelled(d, on)),
      [update],
    ),
    addHousemate: useCallback(
      (id: string) =>
        update((d) => ({
          ...d,
          picked: [...new Set([...d.picked, id])],
          addedHousemates: [...new Set([...d.addedHousemates, id])],
        })),
      [update],
    ),
    replaceRecipients: useCallback(
      (ids: readonly string[], roster: readonly VolunteerRow[]): HandoffNote => {
        // What is left out depends on the people alone, not on the draft.
        const { note } = handoff(emptyDraft(), ids, roster);
        update((d) => handoff(d, ids, roster).draft);
        return note;
      },
      [update],
    ),
    /**
     * After a send: the stored copy goes at once, and so does a write still
     * waiting to happen — otherwise it would put the sent draft back, one click
     * away from sending it a second time.
     */
    clear: useCallback(() => {
      if (pending.current !== null) {
        clearTimeout(pending.current);
        pending.current = null;
      }
      const id = latest.current.coordinatorId;
      if (id !== null) {
        try {
          localStorage.removeItem(storageKey(id));
        } catch {
          // Nothing stored to lose.
        }
      }
      update(() => emptyDraft(), true);
    }, [update]),
  };
}
